import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import router from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  AssetInterior,
  LAND_DIRECTION,
  OtherPurposeType,
  PurposeType,
} from '@app/constants/property';
import { cn, humanShorten, timeAgo } from '@app/libs/utils';
import geocodingService from '@app/services/geocoding.service';

import Avatar from '../dashboard/Avatar';
import ArrowDownIcon from '../icons/ArrowDownIcon';
import FilterIcon from '../icons/FilterIcon';
import LocationIcon from '../icons/LocationIcon';
import MapIcon from '../icons/MapIcon';
import ResetIcon from '../icons/ResetIcon';
import Checkbox from '../ui/Checkbox';
import Dropdown from '../ui/Dropdown';
import Tooltip from '../ui/Tooltip';

import { AdministrativeSelect } from './AdministrativeSelect';
import { DropPanel } from './DropPanel';
import { MapSearch } from './MapSearch';
import { RangeSelect } from './RangeSelect';
import { Thumbnail } from './Thumbnail';

interface AdvanceSearchProps {
  className?: string;
  onSearch: (params: any) => void;
  properties: any;
}

export const AdvanceSearch: React.FC<AdvanceSearchProps> = ({
  className,
  onSearch,
  properties,
}) => {
  const queryParams = useSearchParams();
  const [params, setParams] = useState<any>({});
  const [showMap, setShowMap] = useState<boolean>();
  const firstShowMap = useRef<boolean>(false);
  const addressRef = useRef<string>('');
  const [markers, setMarkers] = useState<any[]>([]);
  const isInitialMount = useRef<boolean>(true);
  const isSettingParamsFromQuery = useRef<boolean>(false);
  const initShowMap = useRef<boolean>(true);

  useEffect(() => {
    if (!properties) return;

    setMarkers(
      properties.map((property: any) => ({
        lat: property.centerPoint?.x,
        lng: property.centerPoint?.y,
        toolip: <div>{humanShorten(property.price)}</div>,
        popup: (
          <div className="flex flex-col gap-2 overflow-hidden max-w-65">
            <div className="images w-65 h-[167px] overflow-hidden">
              <Thumbnail
                images={property.images}
                alt={property.title}
                width={260}
                height={167}
                imageClassName="object-cover"
              />
            </div>
            <div className="text-sm font-bold text-gray-900">{property.title}</div>
            <div className="text-sm text-gray-500 text-truncate text-ellipsis line-clamp-2 flex items-center gap-1">
              <LocationIcon className="w-3.5 h-3.5" />
              {property.address}
            </div>
            <div className="flex items-center gap-1 text-sm text-gray-500">
              {property.area} m² ◆{' '}
              <span className="font-bold">{humanShorten(property.price, 1)}</span>
            </div>
            <div className="text-sm text-gray-500 border-t border-gray-300 pt-2 flex gap-2">
              <Avatar
                userInfo={{ ...property.contactInformation, avatar: property.avatar || 'text' }}
                width={32}
                height={32}
                className="w-8 h-8 rounded-full overflow-hidden text-lg bg-gray-200"
              />
              <div className="flex flex-col gap-1">
                <span className="text-gray-800 text-xs font-bold">
                  {property.contactInformation.alias ||
                    property.contactInformation.fullName ||
                    property.contactInformation.name}
                </span>
                <span className="text-xs text-gray-500">{timeAgo(property.createdAt)}</span>
              </div>
            </div>
            <div className="text-xs text-gray-500 text-sm underline text-right flex items-center gap-1 justify-end">
              <Link href={`/properties/${property.id}`} className="text-sm text-gray-500">
                Xem chi tiết
              </Link>
            </div>
          </div>
        ),
      }))
    );
  }, [properties]);

  const typeOptions = useMemo(
    () =>
      [...Object.entries(PurposeType), ...Object.entries(OtherPurposeType)].map(([key, value]) => ({
        value: key,
        label: value,
      })),
    []
  );

  useEffect(() => {
    // Bỏ qua lần mount đầu tiên vì dữ liệu đã được load từ server-side
    if (isInitialMount.current || isSettingParamsFromQuery.current) {
      isSettingParamsFromQuery.current = false;
      return;
    }

    if (params.lat && params.lng && params.distance !== undefined) {
      onSearch?.({ ...params });
    }
  }, [params]);

  const bedsOptions = useMemo(
    () => [
      { value: '1', label: '1 phòng ngủ' },
      { value: '2', label: '2 phòng ngủ' },
      { value: '3', label: '3 phòng ngủ' },
      { value: '4', label: '4 phòng ngủ' },
      { value: '>4', label: 'Nhiều hơn 4 phòng ngủ' },
    ],
    []
  );

  const bathsOptions = useMemo(
    () => [
      { value: '1', label: '1 phòng tắm / vệ sinh' },
      { value: '2', label: '2 phòng tắm / vệ sinh' },
      { value: '3', label: '3 phòng tắm / vệ sinh' },
      { value: '4', label: '4 phòng tắm / vệ sinh' },
      { value: '>4', label: 'Nhiều hơn 4 phòng tắm / vệ sinh' },
    ],
    []
  );

  const confirmOptions = useMemo(
    () => [
      { value: undefined, label: 'Tất cả tin' },
      { value: 'true', label: 'Đã xác thực' },
      { value: 'false', label: 'Chưa xác thực' },
    ],
    []
  );

  const priceOptions = useMemo(
    () => [
      { value: 1, label: 'Dưới 5 tỷ', min: 0, max: 5_000_000_000 },
      { value: 2, label: '5 tỷ - 10 tỷ', min: 5_000_000_000, max: 10_000_000_000 },
      { value: 3, label: '10 tỷ - 15 tỷ', min: 10_000_000_000, max: 15_000_000_000 },
      { value: 4, label: '15 tỷ - 20 tỷ', min: 15_000_000_000, max: 20_000_000_000 },
      { value: 5, label: 'Trên 20 tỷ', min: 20_000_000_000, max: undefined },
    ],
    []
  );

  const areaOptions = useMemo(
    () => [
      { value: 1, label: 'Dưới 50 m²', min: 0, max: 50 },
      { value: 2, label: '50 m² - 100 m²', min: 50, max: 100 },
      { value: 3, label: '100 m² - 150 m²', min: 100, max: 150 },
      { value: 4, label: '150 m² - 200 m²', min: 150, max: 200 },
      { value: 5, label: 'Trên 200 m²', min: 200, max: undefined },
    ],
    []
  );

  const interiorOptions = useMemo(
    () => Object.entries(AssetInterior).map(([value, label]) => ({ value, label })),
    []
  );

  useEffect(() => {
    if (showMap === false) {
      setParams((prev: any) => ({ ...prev, lat: undefined, lng: undefined, distance: undefined }));
      onSearch?.({ ...params, lat: undefined, lng: undefined, distance: undefined });

      return;
    }

    if (!addressRef.current) {
      if (firstShowMap.current) {
        onSearch?.({ ...params, lat: 21.0285, lng: 105.8542, distance: params.distance ?? 1000 });
        firstShowMap.current = false;
      }

      return;
    }

    firstShowMap.current = false;

    const handleGeocode = async () => {
      let geoParams = { ...params };
      if (!showMap) return;

      try {
        const result = await geocodingService.geocodeAddress(addressRef.current);
        if (result) {
          geoParams = {
            ...geoParams,
            lat: result.lat,
            lng: result.lng,
            distance: params.distance ?? 1000,
          };
        }
      } catch {
        geoParams = {
          ...geoParams,
          lat: 21.0285,
          lng: 105.8542,
          distance: params.distance ?? 1000,
        };
      }

      setParams(geoParams);
    };

    handleGeocode();
  }, [showMap, addressRef.current]);

  useEffect(() => {
    const params = Object.fromEntries(queryParams.entries()) as any;

    params.provinceCodes = params.provinceCodes?.split(',') ?? [];
    params.wardCodes = params.wardCodes?.split(',') ?? [];
    params.orientation = params.orientation?.split(',') ?? [];
    params.types = params.types?.split(',') ?? [];
    params.furnitureType = params.furnitureType?.split(',') ?? [];

    firstShowMap.current = params.lat && params.lng && params.distance !== undefined;
    setShowMap(params.lat && params.lng && params.distance !== undefined);

    // Đánh dấu đang set params từ queryParams để useEffect của params không gọi onSearch
    if (isInitialMount.current) {
      isSettingParamsFromQuery.current = true;
      isInitialMount.current = false;
    }

    setParams(params);
  }, [queryParams]);

  const handleSearch = () => {
    onSearch?.(params);
  };

  const handleReset = () => {
    setParams({});
    window.history.replaceState(null, '', '/properties');

    onSearch?.({});
  };

  return (
    <div
      className={cn('w-full', className, showMap ? '!fixed top-20 left-0 !z-100 !w-screen' : '')}
    >
      <div
        className={cn(
          'grid grid-cols-1 2xl:grid-cols-4 gap-3',
          showMap
            ? 'absolute top-3 left-1/2 -translate-x-1/2 z-101 w-full max-w-400 pl-12 pr-4'
            : ''
        )}
      >
        <div className="flex w-full gap-3 relative z-100">
          <input
            type="text"
            value={params.keyword}
            onChange={e => setParams({ ...params, keyword: e.target.value })}
            placeholder="Nhập từ khóa để tìm kiếm..."
            className="grow border border-gray-300 rounded-lg bg-white px-5 h-12 text-sm leading-relaxed text-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 transition-all shadow"
          />
          <button
            className="rounded-lg bg-blue-800 text-white hover:bg-blue-900 font-medium transition-colors cursor-pointer px-4 flex items-center h-12 block 2xl:hidden"
            onClick={handleSearch}
          >
            Tìm kiếm
          </button>
        </div>
        <div className="col-span-1 2xl:col-span-3 flex flex-col sm:flex-row gap-3 flex-wrap relative z-100">
          <Dropdown
            options={confirmOptions}
            value={params.confirm}
            onChange={value => setParams({ ...params, confirm: value })}
            triggerClassName="h-12 rounded-lg text-gray-700 border border-gray-300 font-bold transition-colors cursor-pointer bg-white px-4 flex hover:shadow-md grow shadow"
            className="grow"
            dropdownClassName="min-w-78 z-1000"
          >
            <>
              <button className="border-none bg-transparent overflow-hidden text-xs font-normal flex items-center flex-nowrap w-25">
                {confirmOptions.find(option => option.value === params.confirm)?.label ||
                  'Tất cả tin'}
              </button>
              <ArrowDownIcon className="w-4 h-4 group-[.open]:rotate-180 transition-transform text-gray-700" />
            </>
          </Dropdown>
          <AdministrativeSelect
            triggerClassName="hover:shadow-md rounded-lg cursor-pointer h-12 bg-white shadow"
            provinceCodes={params.provinceCodes}
            wardCodes={params.wardCodes}
            className="grow"
            onChange={useCallback(
              (provinceCodes: string[], wardCodes: string[], address: string) => {
                addressRef.current = address;
                setParams((prev: any) => ({ ...prev, provinceCodes, wardCodes }));
              },
              []
            )}
          />
          <div className="flex flex-col sm:flex-row gap-3">
            <Dropdown
              options={typeOptions}
              value={params.types}
              onChange={value => setParams({ ...params, types: value as string[] })}
              multiple
              triggerClassName="h-12 rounded-lg text-gray-700 border border-gray-300 font-bold transition-colors cursor-pointer bg-white px-4 flex hover:shadow-md shadow"
              dropdownClassName="min-w-78 z-1000"
              allOption="Tất cả loại Bất động sản"
            >
              <>
                <button className="border-none bg-transparent overflow-hidden text-xs font-normal flex items-center flex-nowrap">
                  Loại &nbsp;
                  <span
                    className="text-gray-500 w-7 flex"
                    dangerouslySetInnerHTML={{
                      __html: params.types?.length ? `(${params.types.length})` : '&nbsp;',
                    }}
                  ></span>
                </button>
                <ArrowDownIcon className="w-4 h-4 group-[.open]:rotate-180 transition-transform text-gray-700" />
              </>
            </Dropdown>

            <RangeSelect
              label="Khoảng giá"
              start={params.fromPrice ?? 0}
              end={params.toPrice}
              min={5_000_000_000}
              step={100_000_000}
              max={20_000_000_000}
              onChange={(fromPrice, toPrice) => setParams({ ...params, fromPrice, toPrice })}
              triggerClassName="hover:shadow-md h-12 shadow"
              dropdownClassName="min-w-78 max-h-none"
            >
              <ul className="flex flex-col gap-2 mt-5 gap-4">
                <li>
                  <Checkbox
                    type="radio"
                    label="Tất cả giá"
                    checked={(params.fromPrice ?? 0) === 0 && params.toPrice === undefined}
                    onChange={() =>
                      setParams({ ...params, fromPrice: undefined, toPrice: undefined })
                    }
                  />
                </li>
                {priceOptions.map(option => (
                  <li key={option.value}>
                    <Checkbox
                      type="radio"
                      label={option.label}
                      checked={
                        (params.fromPrice ?? 0) >= option.min &&
                        (params.toPrice ?? Infinity) <= (option.max ?? Infinity)
                      }
                      onChange={() =>
                        setParams({ ...params, fromPrice: option.min, toPrice: option.max })
                      }
                    />
                  </li>
                ))}
              </ul>
            </RangeSelect>

            <RangeSelect
              label="Diện tích"
              start={params.fromArea ?? 0}
              end={params.toArea}
              min={10}
              step={10}
              max={200}
              unit=" m²"
              onChange={(fromArea, toArea) => setParams({ ...params, fromArea, toArea })}
              triggerClassName="hover:shadow-md h-12 shadow"
              dropdownClassName="min-w-78 max-h-none"
            >
              <ul className="flex flex-col gap-2 mt-5 gap-4">
                <li>
                  <Checkbox
                    type="radio"
                    label="Tất cả diện tích"
                    checked={(params.fromArea ?? 0) === 0 && params.toArea === undefined}
                    onChange={() =>
                      setParams({ ...params, fromArea: undefined, toArea: undefined })
                    }
                  />
                </li>
                {areaOptions.map(option => (
                  <li key={option.value}>
                    <Checkbox
                      type="radio"
                      label={option.label}
                      checked={
                        (params.fromArea ?? 0) >= option.min &&
                        (params.toArea ?? Infinity) <= (option.max ?? Infinity) &&
                        (params.toArea ?? Infinity) > option.min
                      }
                      onChange={() =>
                        setParams({ ...params, fromArea: option.min, toArea: option.max })
                      }
                    />
                  </li>
                ))}
              </ul>
            </RangeSelect>
          </div>

          <div className="flex gap-3">
            <DropPanel
              trigger={
                <Tooltip content="Bộ lọc nâng cao">
                  <button className="h-12 rounded-lg text-gray-700 border border-gray-300 font-bold transition-colors cursor-pointer bg-white px-4 flex items-center justify-center hover:shadow-md shadow">
                    <FilterIcon className="w-6 h-6 text-gray-500" />
                  </button>
                </Tooltip>
              }
              dropdownClassName="min-w-78"
            >
              <Dropdown
                options={LAND_DIRECTION as any}
                value={params.orientation}
                onChange={value => setParams({ ...params, orientation: value })}
                triggerClassName="h-12 rounded-lg text-gray-700 border border-gray-300 font-bold transition-colors cursor-pointer bg-white px-4 flex shadow"
                multiple
                dropdownClassName="min-w-78"
                allOption="Tất cả hướng"
              >
                <>
                  <button className="border-none bg-transparent overflow-hidden text-xs font-normal flex items-center flex-nowrap">
                    Hướng &nbsp;
                    <span
                      className="text-gray-500 w-7 flex"
                      dangerouslySetInnerHTML={{
                        __html: params.orientation ? `(${params.orientation.length})` : '&nbsp;',
                      }}
                    ></span>
                  </button>
                  <ArrowDownIcon className="w-4 h-4 group-[.open]:rotate-180 transition-transform text-gray-700" />
                </>
              </Dropdown>

              <Dropdown
                options={bedsOptions}
                value={params.bedRoom}
                onChange={value => setParams({ ...params, bedRoom: value })}
                triggerClassName="h-12 rounded-lg text-gray-700 border border-gray-300 font-bold transition-colors cursor-pointer bg-white px-4 flex shadow"
                dropdownClassName="min-w-78"
                allOption="Tất cả"
              >
                <>
                  <button className="border-none bg-transparent overflow-hidden text-xs font-normal flex items-center">
                    Phòng ngủ &nbsp;
                    <span
                      className="text-gray-500 w-7 flex"
                      dangerouslySetInnerHTML={{
                        __html: params.bedRoom ? `(${params.bedRoom})` : '&nbsp;',
                      }}
                    ></span>
                  </button>
                  <ArrowDownIcon className="w-4 h-4 group-[.open]:rotate-180 transition-transform text-gray-700" />
                </>
              </Dropdown>

              <Dropdown
                options={bathsOptions}
                value={params.bathRoom}
                onChange={value => setParams({ ...params, bathRoom: value })}
                triggerClassName="h-12 rounded-lg text-gray-700 border border-gray-300 font-bold transition-colors cursor-pointer bg-white px-4 flex shadow"
                dropdownClassName="min-w-78"
                allOption="Tất cả"
              >
                <>
                  <button className="border-none bg-transparent overflow-hidden text-xs font-normal flex items-center flex-nowrap">
                    Phòng tắm / vệ sinh &nbsp;
                    <span
                      className="text-gray-500 w-7 flex"
                      dangerouslySetInnerHTML={{
                        __html: params.bathRoom ? `(${params.bathRoom})` : '&nbsp;',
                      }}
                    ></span>
                  </button>
                  <ArrowDownIcon className="w-4 h-4 group-[.open]:rotate-180 transition-transform text-gray-700" />
                </>
              </Dropdown>

              <Dropdown
                options={interiorOptions}
                value={params.furnitureType}
                onChange={value => setParams({ ...params, furnitureType: value })}
                triggerClassName="h-12 rounded-lg text-gray-700 border border-gray-300 font-bold transition-colors cursor-pointer bg-white px-4 flex shadow"
                dropdownClassName="min-w-78"
                allOption="Tất cả"
                multiple
              >
                <>
                  <button className="border-none bg-transparent overflow-hidden text-xs font-normal flex items-center flex-nowrap">
                    Nội thất &nbsp;
                    <span
                      className="text-gray-500 w-7 flex"
                      dangerouslySetInnerHTML={{
                        __html: params.furnitureType
                          ? `(${params.furnitureType.length})`
                          : '&nbsp;',
                      }}
                    ></span>
                  </button>
                  <ArrowDownIcon className="w-4 h-4 group-[.open]:rotate-180 transition-transform text-gray-700" />
                </>
              </Dropdown>
            </DropPanel>
            <Tooltip content="Xóa tất cả bộ lọc">
              <button
                className={cn(
                  'h-12 rounded-lg text-gray-700 border border-gray-300 font-bold transition-colors cursor-pointer bg-white px-4 shadow',
                  'flex items-center justify-center',
                  'hover:shadow-md'
                )}
                onClick={handleReset}
              >
                <ResetIcon className="w-4 h-4" />
              </button>
            </Tooltip>

            <Tooltip content="Xem trên bản đồ">
              <button
                className={cn(
                  'h-12 rounded-lg font-bold transition-colors cursor-pointer bg-white px-4 flex items-center justify-center hover:shadow-md shadow',
                  showMap
                    ? 'border border-blue-200 text-gray-700 bg-blue-200'
                    : 'border border-gray-300 text-gray-700'
                )}
                onClick={() => setShowMap(prev => !prev)}
              >
                <MapIcon className="w-4 h-4" />
              </button>
            </Tooltip>
            <button
              className="rounded-lg bg-blue-800 text-white hover:bg-blue-900 font-medium transition-colors cursor-pointer px-4 flex items-center h-12 hidden 2xl:flex text-nowrap"
              onClick={handleSearch}
            >
              Tìm kiếm
            </button>
          </div>
        </div>
      </div>

      {showMap && (
        <MapSearch
          className="col-span-full !fixed top-20 left-0 w-screen h-[calc(100vh-80px)]"
          onSearch={handleSearch}
          radius={params.distance ?? 1000}
          lat={params.lat ?? 21.0285}
          lng={params.lng ?? 105.8542}
          onLocationChange={(lat, lng, distance) => {
            setParams((prev: any) => ({ ...prev, lat, lng, distance }));
          }}
          markers={markers}
        />
      )}
    </div>
  );
};
